import React from "react";
import { translateValidation } from "../../utils/translateValidation";

type FieldSize = "sm" | "md";
type FieldLayout = "stack" | "inline";

interface FormFieldProps {
  label?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
  /** Layout: "stack" (label above, default) or "inline" (label left of input — ERP style). */
  layout?: FieldLayout;
  /** Compact ERP density when true / "sm". */
  size?: FieldSize;
  /** Fixed label column width in tailwind units (only used when layout="inline"). Default 32. */
  labelWidth?: string;
  /** Optional helper text shown below the input. */
  hint?: string;
  /**
   * Sits immediately after the label text — an info tip, a small badge.
   *
   * Part of the label row rather than of the control, so it stays beside the
   * word it explains when the inline layout stacks on a narrow screen.
   */
  labelAdornment?: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  required = false,
  children,
  className,
  layout = "stack",
  size = "md",
  labelWidth = "w-32",
  hint,
  labelAdornment,
}) => {
  // One line under the control: the error if there is one, otherwise the hint.
  // The hint explains the field, the error explains this value, and both under
  // one box is noise. `size` is only consulted for the error — a hint has always
  // been the small text regardless of the field's density.
  const note = error
    ? {
        text: error,
        tone: "text-[#E24B4A]",
        size: size === "sm" ? "text-[11px]" : "text-sm",
      }
    : hint
      ? { text: hint, tone: "text-[#8A9590]", size: "text-[11px]" }
      : null;
  if (layout === "inline") {
    const labelTextSize = size === "sm" ? "text-[12px]" : "text-xs";
    // The label sits beside the control only while there is room for both.
    // Two independent tests decide that, because a field can be cramped for
    // two different reasons:
    //
    //   max-sm:    the viewport itself is phone-width
    //   @max-sm:   the *column* this field sits in is narrow, even though the
    //              window is wide — a three-across route card on a laptop, say
    //
    // The container query needs an ancestor declaring `@container` (SectionCard
    // does). Where there is none it simply never matches, so fields outside a
    // container keep the plain side-by-side layout they have always had.
    return (
      <div
        className={`flex items-start gap-2 max-sm:flex-col max-sm:gap-0.5 @max-sm:flex-col @max-sm:gap-0.5 ${className ?? ""}`}
      >
        {label && (
          <label
            className={`shrink-0 ${labelWidth} max-sm:w-full @max-sm:w-full ${labelTextSize} font-medium text-[#8A9590] pt-2 max-sm:pt-1 @max-sm:pt-1 leading-tight select-none inline-flex items-center gap-1`}
          >
            <span>
              {label}
              {required && <span className="text-[#E24B4A] ml-0.5">*</span>}
            </span>
            {labelAdornment}
          </label>
        )}
        {/* Named container so a control can lay its own sub-fields out against
            the space it actually got, rather than against the viewport. */}
        <div className="flex-1 min-w-0 w-full @container/control">
          {children}
          {note && (
            <p className={`mt-0.5 text-[11px] ${note.tone}`}>
              {translateValidation(note.text)}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-1 ${className ?? ""}`}>
      {label && (
        <label
          className={`flex items-center gap-1 ${
            size === "sm" ? "text-[12px]" : "text-sm"
          } font-medium text-[#8A9590]`}
        >
          <span>
            {label}
            {required && <span className="text-[#E24B4A] ml-1">*</span>}
          </span>
          {labelAdornment}
        </label>
      )}
      {children}
      {note && (
        <p className={`${note.size} ${note.tone}`}>
          {translateValidation(note.text)}
        </p>
      )}
    </div>
  );
};

const sizeClasses = (size?: FieldSize) =>
  size === "sm"
    ? "h-8 px-2.5 py-1 text-[13px] rounded-md"
    : "px-3 py-2 rounded-lg";

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  error?: boolean;
  size?: FieldSize;
}

export const Input: React.FC<InputProps> = ({
  error,
  className = "",
  size,
  ...props
}) => {
  // Border color is conditional rather than layered. If we kept the grey
  // `border-[rgba(11,15,14,0.15)]` in baseClasses and only appended the red
  // `border-[#E24B4A]` in errorClasses, Tailwind's compiled-CSS ordering
  // (not the order in className) would decide which wins — and the grey
  // sometimes won, leaving the input grey at rest and only flashing red on
  // focus. Picking exactly one border-color per state avoids the fight.
  const borderColor = error
    ? "border-[#E24B4A]"
    : "border-[rgba(11,15,14,0.15)]";
  const focusRing = error ? "focus:ring-[#E24B4A]" : "focus:ring-[#C97A3C]";
  // A field that cannot be typed into keeps its grey on focus. `focus:bg-white`
  // would otherwise repaint it as an editable box the moment it is clicked —
  // which is exactly when someone is deciding whether they can edit it. The
  // focus ring stays: the field is still focusable, and can still be copied.
  const inert = props.readOnly || props.disabled;
  const focusBg = inert ? "" : "focus:bg-white";
  const baseClasses = `w-full border ${borderColor} focus:ring-2 ${focusRing} focus:border-transparent placeholder-[#8A9590] bg-[#FFFFFF] ${focusBg} text-[#0B0F0E] ${sizeClasses(size)}`;
  const readOnlyClasses = inert
    ? "bg-[#F5F4F0] text-[#8A9590] cursor-not-allowed"
    : "";

  return (
    <input
      className={`${baseClasses} ${className} ${readOnlyClasses}`}
      {...props}
    />
  );
};

interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> {
  error?: boolean;
  options?: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  size?: FieldSize;
}

export const Select: React.FC<SelectProps> = ({
  error,
  options = [],
  placeholder,
  className = "",
  size,
  children,
  ...props
}) => {
  // See Input above: pick one border-color per state instead of layering
  // grey + red, otherwise Tailwind's class-ordering picks grey at rest.
  const borderColor = error
    ? "border-[#E24B4A]"
    : "border-[rgba(11,15,14,0.15)]";
  const focusRing = error ? "focus:ring-[#E24B4A]" : "focus:ring-[#C97A3C]";
  const baseClasses = `w-full border ${borderColor} focus:ring-2 ${focusRing} focus:border-transparent min-w-[80px] bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] ${sizeClasses(size)}`;
  const disabledClasses = props.disabled
    ? "bg-[#F5F4F0] text-[#8A9590] cursor-not-allowed"
    : "";

  return (
    <select
      className={`${baseClasses} ${className} ${disabledClasses}`}
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {children ||
        options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
    </select>
  );
};

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  size?: FieldSize;
  /**
   * Grow with the text rather than clip it. `rows` stays the minimum, so a
   * short note is still a short box — it is long pasted text that would
   * otherwise sit half-hidden behind a scrollbar, cut through the middle of a
   * line, which is unreadable on a page whose whole job is reading.
   */
  autoGrow?: boolean;
  /** Ceiling for `autoGrow`, in px. Past it the box scrolls after all. */
  maxHeight?: number;
  /**
   * Ceiling for `autoGrow` in lines, measured off the box's own line height —
   * so "three lines" is three lines whatever the density. Wins over
   * `maxHeight` when both are given.
   */
  maxRows?: number;
}

export const Textarea: React.FC<TextareaProps> = ({
  error,
  className = "",
  size,
  autoGrow,
  maxHeight = 400,
  maxRows,
  ...props
}) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  // Before paint: the box is measured at its natural height, then set to it.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !autoGrow) return;
    el.style.height = "auto";

    let ceiling = maxHeight;
    if (maxRows) {
      const style = window.getComputedStyle(el);
      const line = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
      // clientHeight already holds the padding; offset − client is the border.
      const frame =
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        (el.offsetHeight - el.clientHeight);
      ceiling = Math.round(line * maxRows + frame);
    }

    const wanted = el.scrollHeight;
    el.style.height = `${Math.min(wanted, ceiling)}px`;
    el.style.overflowY = wanted > ceiling ? "auto" : "hidden";
  }, [autoGrow, maxHeight, maxRows, props.value, props.rows]);
  const padding =
    size === "sm"
      ? "px-2.5 py-1.5 text-[13px] rounded-md"
      : "px-3 py-2 rounded-lg";
  // See Input above: one border-color per state, not layered.
  const borderColor = error
    ? "border-[#E24B4A]"
    : "border-[rgba(11,15,14,0.15)]";
  const focusRing = error ? "focus:ring-[#E24B4A]" : "focus:ring-[#C97A3C]";
  // See Input above.
  const inert = props.readOnly || props.disabled;
  const focusBg = inert ? "" : "focus:bg-white";
  const resize = autoGrow ? "resize-none" : "resize-y";
  const baseClasses = `w-full border ${borderColor} focus:ring-2 ${focusRing} focus:border-transparent placeholder-[#8A9590] bg-[#FFFFFF] ${focusBg} text-[#0B0F0E] ${resize} ${padding}`;
  const readOnlyClasses = inert
    ? "bg-[#F5F4F0] text-[#8A9590] cursor-not-allowed"
    : "";

  return (
    <textarea
      ref={ref}
      className={`${baseClasses} ${className} ${readOnlyClasses}`}
      {...props}
    />
  );
};
